import { ChevronDown, ChevronRight } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/base/badges/badges"
import { Button } from "@/components/base/buttons/button"

import { useReplay } from "./ReplayProvider"

export function EventInspector() {
  const { events, currentEventIndex, jumpToEvent, jumpToCursor } = useReplay()
  const [expandedPayload, setExpandedPayload] = useState(true)
  const [expandedCorrelates, setExpandedCorrelates] = useState(true)

  const currentEvent = events[currentEventIndex]

  if (!currentEvent) {
    return (
      <div className="event-inspector rounded-lg bg-white p-4 shadow-sm dark:bg-gray-900">
        <h3 className="mb-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
          Event Inspector
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No event selected
        </p>
      </div>
    )
  }

  const formatTimestamp = (ts: number) => {
    const d = new Date(ts)
    const pad = (n: number) => String(n).padStart(2, "0")
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }

  const getEventColor = ():
    | "gray"
    | "brand"
    | "error"
    | "warning"
    | "success"
    | "gray-blue"
    | "blue-light"
    | "blue"
    | "indigo"
    | "purple"
    | "pink"
    | "orange" => {
    const type = currentEvent.type
    if (type.includes("user")) return "blue"
    if (type.includes("npc")) return "success"
    if (type.includes("coach")) return "purple"
    if (type.includes("stage")) return "orange"
    if (type.includes("minigame")) return "pink"
    if (type.includes("safety")) return "error"
    return "gray"
  }

  const renderJson = (data: unknown) => {
    if (!data) return <span className="text-gray-400">null</span>

    try {
      return (
        <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-xs dark:bg-gray-800">
          {JSON.stringify(data, null, 2)}
        </pre>
      )
    } catch {
      return <span className="text-gray-500">Invalid JSON</span>
    }
  }

  return (
    <div className="event-inspector space-y-6 rounded-xl bg-white p-6 shadow-md dark:bg-gray-900">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Event Inspector
        </h3>
        <Badge type="pill-color" color={getEventColor()} size="md">
          {currentEvent.type}
        </Badge>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 border-b border-gray-200 pb-4 dark:border-gray-700">
        <Button
          color="tertiary"
          size="md"
          isDisabled={currentEventIndex === 0}
          onClick={() => {
            const prevIndex = Math.max(0, currentEventIndex - 1)
            const target = events[prevIndex]
            if (target?.event_id) jumpToEvent(target.event_id)
            else if (target?.cursor) jumpToCursor(target.cursor)
          }}
        >
          Previous
        </Button>
        <Button
          color="tertiary"
          size="md"
          isDisabled={currentEventIndex >= events.length - 1}
          onClick={() => {
            const nextIndex = Math.min(events.length - 1, currentEventIndex + 1)
            const target = events[nextIndex]
            if (target?.event_id) jumpToEvent(target.event_id)
            else if (target?.cursor) jumpToCursor(target.cursor)
          }}
        >
          Next
        </Button>
      </div>

      {/* Basic Info */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 text-sm">
          <div>
            <span className="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
              Event ID
            </span>
            <p className="font-mono text-xs break-all text-gray-900 dark:text-gray-100">
              {currentEvent.event_id}
            </p>
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
              Cursor
            </span>
            <p className="font-mono text-sm text-gray-900 dark:text-gray-100">
              {currentEvent.cursor}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
              Timestamp
            </span>
            <p className="font-mono text-xs text-gray-900 dark:text-gray-100">
              {formatTimestamp(currentEvent.server_ts)}
            </p>
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
              Stage
            </span>
            <p className="font-mono text-xs text-gray-900 dark:text-gray-100">
              {currentEvent.stage ?? "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Correlates */}
      {currentEvent.correlates && (
        <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            onClick={() => setExpandedCorrelates(!expandedCorrelates)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-900 transition-colors hover:text-gray-700 dark:text-gray-100 dark:hover:text-gray-300"
          >
            {expandedCorrelates ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Correlates
          </button>
          {expandedCorrelates && (
            <div className="mt-3">{renderJson(currentEvent.correlates)}</div>
          )}
        </div>
      )}

      {/* Payload */}
      <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
        <button
          onClick={() => setExpandedPayload(!expandedPayload)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-900 transition-colors hover:text-gray-700 dark:text-gray-100 dark:hover:text-gray-300"
        >
          {expandedPayload ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          Payload
        </button>
        {expandedPayload && (
          <div className="mt-3">{renderJson(currentEvent.payload)}</div>
        )}
      </div>
    </div>
  )
}
